import './index.scss';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { lang, textTransformToCapital } from '@umu/umu-util';
import { classSet } from '@umu/umu-dom';
import { CHECKBOX_CHECK_TYPE, ISelectItem } from '../../interface';
import { getDepartmentList, searchDepartmentList, searchGroupList } from '../../server';
import { defaultList } from '../../dataFlow';
import { Input } from '../Input';
import { ListLoading } from 'common/src/components/list/list';
import InfiniteScroll from '../../../@infiniteScroll/infiniteScroll';
import { Checkbox } from '../checkbox';
import merge from 'lodash/merge';

export enum MEMBER_TYPE {
    GROUP,
    DEPARTMENT,
    SEARCH_DEPARTMENT,
    SESSION,
}
interface IProps {
    sourceType?: MEMBER_TYPE;
    defaultValue: string;
    isHideBorder?: boolean;
    extendParams?: Record<string, string>;
    isCustom?: boolean;
    customList?: ISelectItem[];
    onSelect: (key: string) => void;
}

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 100;

const sourceMap = {
    [MEMBER_TYPE.GROUP]: {
        server: searchGroupList,
        label: textTransformToCapital(lang('所有分组')),
        selectLabel: lang('包含{$0}个分组全员'),
        placeholder: lang('输入分组名并回车筛选'),
    },
    [MEMBER_TYPE.DEPARTMENT]: {
        server: getDepartmentList,
        label: textTransformToCapital(lang('所有部门')),
        selectLabel: lang('包含{$0}个部门全员'),
        placeholder: lang('输入部门名并回车筛选'),
    },
    [MEMBER_TYPE.SEARCH_DEPARTMENT]: {
        server: searchDepartmentList,
        label: textTransformToCapital(lang('所有部门')),
        selectLabel: lang('包含{$0}个部门全员'),
        placeholder: lang('输入部门名并回车筛选'),
    },
    [MEMBER_TYPE.SESSION]: {
        server: searchDepartmentList,
        label: textTransformToCapital(lang('所有小节类型')),
        selectLabel: lang('包含{$0}个小节类型'),
        placeholder: lang('输入部门名并回车筛选'),
    },
};

export const MemberSearchSelect = forwardRef((props: IProps, ref: any) => {
    const {
        sourceType = MEMBER_TYPE.GROUP,
        isHideBorder = false,
        extendParams = {},
        defaultValue,
        isCustom = false,
        customList = [],
        onSelect,
    } = props;
    const [list, setList] = useState<ISelectItem[]>([]);
    const [selectList, setSelectList] = useState<ISelectItem[]>([]);
    const [isLoading, setIsLoad] = useState(true);
    const [isFold, setIsFold] = useState(true);
    const [keywords, setKeywords] = useState('');
    const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultList.page_info);

    useImperativeHandle(ref, () => ({
        clear: () => {
            onClear();
        },
    }));

    useEffect(() => {
        initData(DEFAULT_PAGE, keywords);
        if (defaultValue === '') {
            setSelectList([]);
        }
    }, [defaultValue]);

    const initData = async (page: number, val: string) => {
        if (isCustom) {
            setIsLoad(false);
            setList(customList);
            if (defaultValue) {
                const arr = defaultValue.split(',');
                setSelectList(customList.filter((_) => arr.indexOf(_.id) !== -1));
            }
            return;
        }
        let typeIndex = sourceType;
        if (sourceType === MEMBER_TYPE.DEPARTMENT && val !== '') {
            typeIndex = MEMBER_TYPE.SEARCH_DEPARTMENT;
        }
        if (val !== keywords) {
            setKeywords(val);
        }
        const params = merge({}, { keywords: val, page, size: DEFAULT_SIZE }, extendParams);
        const res = await sourceMap[typeIndex].server(params);
        setPageInfo(res.page_info);
        const data = res.list.map((_) => {
            return {
                id: _.id || _.department_id,
                label: _.group_name || _.department_name,
            };
        });
        setList(res.page_info.current_page === 1 ? data : list.concat(data));
        setIsLoad(false);
    };
    const getLoadMore = () => {
        if (isCustom) {
            return;
        }
        if (pageInfo.current_page < pageInfo.total_page_num && !isLoading) {
            setIsLoad(true);
            initData(pageInfo.current_page + 1, keywords);
        }
    };
    const onClear = () => {
        setIsFold(true);
        setSelectList([]);
        onSelect('');
    };
    const onSelectedChange = (isChecked: boolean, data: ISelectItem) => {
        let newList = [...selectList];
        const isHas = !!selectList.find((item) => data.id === item.id);
        if (isChecked && !isHas) {
            newList.push(data);
            setSelectList(newList);
        }
        if (!isChecked && isHas) {
            newList = newList.filter((_) => _.id !== data.id);
            setSelectList(newList);
        }
        onSelect(newList.map((_) => _.id).toString());
    };
    const renderLabel = () => {
        const source = sourceMap[sourceType];
        const count = selectList.length;
        const label = !count
            ? source.label
            : count === 1
            ? selectList[0].label
            : source.selectLabel.replace('{$0}', String(count));

        return <div className="member-select-label">{label}</div>;
    };
    const renderClear = () => {
        return !!selectList.length ? <i className="icon icon-clear" onClick={onClear} /> : null;
    };

    const renderList = () => {
        return !!list.length ? (
            list.map((_, index: number) => {
                const isSelect = !!selectList.find((item) => _.id === item.id);
                return (
                    <li
                        key={`member-${_.id}-${index}`}
                        className={classSet({
                            'member-li': true,
                            'member-li-active': isSelect,
                        })}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelectedChange(!isSelect, _);
                        }}
                    >
                        <div className="member-check">
                            <Checkbox
                                isChecked={isSelect ? CHECKBOX_CHECK_TYPE.IS_CHECK : CHECKBOX_CHECK_TYPE.UN_CHECK}
                                onChange={(isChecked) => onSelectedChange(isChecked, _)}
                            />
                        </div>
                        <div className="member-label">{_.label}</div>
                    </li>
                );
            })
        ) : isLoading ? (
            <ListLoading />
        ) : (
            <li className="member-li member-empty">{lang('暂无数据')}</li>
        );
    };

    const onKeyDownEnter = (val: string) => {
        initData(DEFAULT_PAGE, val);
    };
    const renderSearchInput = () => {
        return <Input option={{ placeholder: sourceMap[sourceType].placeholder }} onKeyDownEnter={onKeyDownEnter} />;
    };

    return (
        <div className="member-search-select-panel" onMouseLeave={() => setIsFold(true)}>
            <div
                className={classSet({
                    'member-selected': true,
                    'border': !isHideBorder,
                    'member-selected-active': !isFold,
                })}
                onClick={() => setIsFold(!isFold)}
            >
                {renderLabel()}
                {renderClear()}
                <i className={`icon icon-arrow-item ${isFold ? 'up' : 'down'}`} />
            </div>
            {!isFold && (
                <InfiniteScroll
                    load={getLoadMore}
                    hasMore={!!pageInfo && !!pageInfo.total_page_num && pageInfo.total_page_num > pageInfo.current_page}
                    isLoading={isLoading}
                    bottomDistance={70}
                    scrollThreshold={500}
                >
                    <ul className={`${isFold ? 'hide member-search-list' : 'member-search-list'}`}>
                        {!isCustom && <li className="search-member-input">{renderSearchInput()}</li>}
                        {renderList()}
                    </ul>
                </InfiniteScroll>
            )}
        </div>
    );
});
